import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as tariffActions from './tariff.actions';
import * as tariffSelectors from './tariff.selectors';
import * as _operatorsActions from '../../../store/operators/operators.actions';
import * as operatorsSelectors from '../../../store/operators/operators.selectors';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { MessageTranslateService } from '../../../shared/services/message.service';
import { TariffService } from '../services/tariffs.service';
import { DefaultPageSize } from '../../../shared/constants/pagination.constants';
import * as operatorsActions from '../../../store/operators/operators.actions';

export class TariffEffects {
  actions$: Actions = inject(Actions);
  tariffService = inject(TariffService);
  store: Store = inject(Store);
  messageService = inject(MessageTranslateService);

  createTariff$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tariffActions.insertNewTariff),
      switchMap((requestData) =>
        this.tariffService.createTarif(requestData.createTariffRequest).pipe(
          map((res) => {
            if (res.isSuccess) {
              return tariffActions.insertNewTariffSuccess({
                result: res,
              });
            }
            return tariffActions.insertNewTariffFailure({
              error: res.error!,
            });
          })
        )
      )
    )
  );

  loadTariffs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        tariffActions.loadTariffList,
        tariffActions.changeTariffPaginationSuccessful,
        tariffActions.insertNewTariffSuccess,
        tariffActions.deleteNewTariffSuccess,
        tariffActions.updateTariffSuccess,
        operatorsActions.SetCurrentOperatorSuccess
      ),
      concatLatestFrom(() => [
        this.store.select(operatorsSelectors.selectCurrentOperator),
        this.store.select(tariffSelectors.selectTariffsPagination),
      ]),
      switchMap(([_action, selectedOperator, pagination]) =>
        this.tariffService
          .searchTariffs({
            operatorId: selectedOperator!.id!,
            pageIndex: pagination?.pageIndex ?? 0,
            pageSize: pagination?.pageSize ?? DefaultPageSize,
          })
          .pipe(
            map((res) => {
              if (res.isSuccess) {
                return tariffActions.loadTariffListSuccess({
                  tariffsResponse: res.value!,
                });
              } else {
                return tariffActions.loadTariffListFailure({
                  error: res.error!,
                });
              }
            }),
            catchError((err) =>
              of(
                tariffActions.loadTariffListFailure({
                  error: err.message || 'Loading tariffs failed',
                })
              )
            )
          )
      )
    )
  );

  deleteTariff$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tariffActions.deleteNewTariff),
      switchMap((requestData) =>
        this.tariffService.delete(requestData.tariffId).pipe(
          map((res) => {
            if (res.isSuccess) {
              return tariffActions.deleteNewTariffSuccess({
                result: res,
              });
            }
            return tariffActions.deleteNewTariffFailure({
              error: res.error!,
            });
          })
        )
      )
    )
  );

  onUpsertError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          tariffActions.insertNewTariffFailure,
          tariffActions.insertNewTariffFailure
        ),
        tap((err) =>
          err.error
            ? this.messageService.error(err.error)
            : this.messageService.errorTranslate('tariffs.failed-to-update')
        )
      ),
    { dispatch: false }
  );
}
